import React, { useEffect, useRef } from 'react';
import { Typography, Grid, FormLabel, debounce, FormControlLabel } from '@mui/material';
import { useFormik } from 'formik';
import { TextInput } from 'components/Input';
import { currencyMask, currencyRateMask, negativeNumberMask, numberMask } from 'helpers/masks';
import FormSectionHeader from './FormSectionHeader';
import { useSelector } from 'react-redux';
import { getSubmissionDetails, getUwBRReview, getUwBRPropertyValues } from 'application/redux/selectors';
import { Spacing } from 'components/Layout';
import { formatDollar, formatPercent, unformatNumberString } from 'helpers/formatters';
import BrLocationReviewPremiumTable from './PremiumTable/Components/BrLocationReviewPremiumTable';
import { Location } from 'types/location.type';
import BRPropertyModel from './BRPropertyModel';
import { validateLocationReview } from '../Helpers/validations';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import {
    calculateBrPricingTable,
    getFinalRateFromAdditionsAndAdjustments,
    getParticipationAdjustedTotalPremium,
    getReviewLumenParticipationPercent,
    updateDerivedFieldsForBrLocationReview,
} from 'helpers/brCalculationHelper';
import { uwReviewSaveBRReview } from 'application/redux/actions';
import LrCheckbox from 'components/LrCheckbox';
import { COLORS } from '../../../../../../../constants';
import InputLabel from '@mui/material/InputLabel';
import LrSplitButton from '../../../../../../../components/LrSplitButton';
import LrToggleButton from '../../../../../../../components/LrToggleButton/components/LrToggleButton';

interface Props {
    locationBrReview?: any;
    validateLocationReview?: (values: BRPropertyModel) => any;
    onOtherLocationReviewsUpdated: () => void;
}

const LABEL_GRID_WIDTH = 6;
const LABEL_MD_GRID_WIDTH = 5;
const LABEL_LG_GRID_WIDTH = 4;
const LABEL_XL_GRID_WIDTH = 2;
const INPUT_GRID_WIDTH = 6;
const INPUT_LARGE_GRID_WIDTH = 4;
const INPUT_MAX_WIDTH = '10rem';

interface Props {
    location: Location;
    onChange: (propertyValue: BRPropertyModel) => void;
    readOnly: boolean;
}

const BrLocationReviewPricingForm: React.FC<Props> = (props) => {
    // Props
    const { location, locationBrReview, readOnly, onOtherLocationReviewsUpdated } = props;

    // Global State
    const uwBrReview = useSelector(getUwBRReview);
    const brPropertyValues = useSelector(getUwBRPropertyValues);
    const submission = useSelector(getSubmissionDetails);
    const isLeader = uwBrReview.isLeader;
    const { uwReviewUpdateOneBRProperty, uwReviewUpdateAllBrPropertyTriaValues } = useActions(Actions);

    // Local State
    const [totalRateAdditions, setTotalRateAdditions] = React.useState<number>(locationBrReview.totalRateAdditions);
    const [totalRateAdjustment, setTotalRateAdjustment] = React.useState<number>(locationBrReview.totalRateAdjustment);
    const [technicalFinalRate, setTechnicalFinalRate] = React.useState<number>(locationBrReview.technicalFinalRate);
    const [automaticExtensionPeriodNumber, setAutomaticExtensionPeriodNumber] = React.useState<number | null>(null);
    const [automaticExtensionPeriodType, setAutomaticExtensionPeriodType] = React.useState<string>('months');

    // Parse an automaticExtensionPeriod into a number and type on initial load
    useEffect(() => {
        if (locationBrReview?.automaticExtensionPeriod) {
            const parts = locationBrReview.automaticExtensionPeriod.split(' ');
            if (parts.length === 2) {
                const numberPart = Number(parts[0]);
                const typePart = parts[1].toLowerCase();
                if (!isNaN(numberPart) && (typePart === 'months' || typePart === 'days')) {
                    setAutomaticExtensionPeriodNumber(numberPart);
                    setAutomaticExtensionPeriodType(typePart);
                }
            }
        }
    }, [locationBrReview?.automaticExtensionPeriod]);

    const formik = useFormik({
        initialValues: locationBrReview || {},
        onSubmit: (values) => {
            uwReviewUpdateOneBRProperty(values);
        },
        validate: validateLocationReview,
        validateOnMount: true,
    });

    // Custom handler to strip currency symbols for rate fields before persisting to formik
    const handleCurrencyRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const numeric = unformatNumberString(value || '0');
        // store as numeric so backend payload has no $ prefix
        formik.setFieldValue(name, numeric);
    };

    // Set formik value for automaticExtensionPeriod when number or type changes
    useEffect(() => {
        if (automaticExtensionPeriodNumber !== null) {
            formik.setFieldValue(
                'automaticExtensionPeriod',
                automaticExtensionPeriodNumber + ' ' + automaticExtensionPeriodType
            );
        }
    }, [automaticExtensionPeriodNumber, automaticExtensionPeriodType]);

    const handleDerivedFieldsUpdates = () => {
        updateDerivedFieldsForBrLocationReview(
            formik.values,
            location,
            submission,
            Number(
                getReviewLumenParticipationPercent(uwBrReview.policyLimitOfLiability, uwBrReview.lumenLimitOfLiability)
            )
        );
        setTotalRateAdditions(formik.values.totalRateAdditions || 0);
        setTotalRateAdjustment(formik.values.totalRateAdjustment || 0);
        setTechnicalFinalRate(formik.values.technicalFinalRate || 0);
    };

    const handleTriaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue(event.target.name, unformatNumberString(event.target.value));
        formik.setFieldValue('hasTria', unformatNumberString(event.target.value) > 0);
        handleDerivedFieldsUpdates();
        uwReviewUpdateAllBrPropertyTriaValues(event.target.value);
        onOtherLocationReviewsUpdated();
    };

    const debouncedSave = useRef(
        debounce((values: BRPropertyModel) => {
            uwReviewUpdateOneBRProperty(values);
        }, 500)
    ).current;

    useEffect(() => {
        handleDerivedFieldsUpdates();
        debouncedSave(formik.values);
    }, [formik.values, debouncedSave]);

    return (
        <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={5}>
                {/* Zone & Base Rate Section */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Base Rate" />
                    <Grid container spacing={6} alignItems="center" rowSpacing={2}>
                        <Grid item>
                            <Grid container direction="row" justifyContent="start" spacing={2}>
                                <Grid item>
                                    <FormLabel>Zone ID:</FormLabel>
                                </Grid>
                                <Grid item>
                                    <FormLabel>{location.zone}</FormLabel>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" justifyContent="start" spacing={2}>
                                <Grid item>
                                    <FormLabel>Base Rate:</FormLabel>
                                </Grid>
                                <Grid item>
                                    <FormLabel>{formik.values.baseRate}</FormLabel>
                                </Grid>
                            </Grid>
                        </Grid>
                        {/*// Leaving commented out until business is ready to use Modeled Rate per feedback 9/5/25*/}
                        {/*<Grid item>*/}
                        {/*    <Grid container direction="row" alignItems="center" justifyContent="start" spacing={2}>*/}
                        {/*        <Grid item>*/}
                        {/*            <FormLabel style={{ marginTop: 0 }}>Modeled:</FormLabel>*/}
                        {/*        </Grid>*/}
                        {/*        <Grid item>*/}
                        {/*            <TextInput*/}
                        {/*                variant="underlined"*/}
                        {/*                name="modeled"*/}
                        {/*                mask={currencyRateMask}*/}
                        {/*                onChange={formik.handleChange}*/}
                        {/*                style={{ maxWidth: INPUT_MAX_WIDTH }}*/}
                        {/*                value={formik.values.modeled}*/}
                        {/*                error={!!formik.errors.modeled}*/}
                        {/*                onBlur={formik.handleBlur}*/}
                        {/*                readOnly={readOnly}*/}
                        {/*            />*/}
                        {/*        </Grid>*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}
                    </Grid>
                </Grid>
                {/* Adjustments Section */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Adjustments" />
                    <Grid container direction="column">
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Additional CAT Exposure Rate ($):</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="additionalCatExposure"
                                        variant="underlined"
                                        mask={currencyRateMask}
                                        onChange={handleCurrencyRateChange}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        value={formik.values.additionalCatExposure}
                                        error={!!formik.errors.additionalCatExposure}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Renovation Modifier Rate ($):</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="renovationModifier"
                                        variant="underlined"
                                        mask={currencyRateMask}
                                        onChange={handleCurrencyRateChange}
                                        value={formik.values.renovationModifier}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.renovationModifier}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Mid-term Placement Rate ($):</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="midTermPlacement"
                                        variant="underlined"
                                        mask={currencyRateMask}
                                        onChange={handleCurrencyRateChange}
                                        value={formik.values.midTermPlacement}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.midTermPlacement}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Spacing height="1rem" />
                            <Grid container direction="row" spacing={2} alignItems="start">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <Typography variant="body1">Total Rate Additions:</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography variant="body1">
                                        {formatDollar(totalRateAdditions, 3, false)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Spacing height="2rem" />
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Deductible Structure (%):</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="deductibleStructure"
                                        variant="underlined"
                                        mask={negativeNumberMask}
                                        onChange={formik.handleChange}
                                        endAdornment="%"
                                        value={formik.values.deductibleStructure || ''}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.deductibleStructure}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Contractor Quality (%):</FormLabel>
                                </Grid>
                                <Grid item>
                                    <TextInput
                                        name="contractorQuality"
                                        variant="underlined"
                                        mask={negativeNumberMask}
                                        onChange={formik.handleChange}
                                        endAdornment="%"
                                        value={formik.values.contractorQuality || ''}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.contractorQuality}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <LrCheckbox
                                        name="hasEnr400Contractor"
                                        checked={formik.values.hasEnr400Contractor}
                                        onChange={formik.handleChange}
                                        style={{ marginLeft: '2rem' }}
                                        disabled={readOnly}
                                    />
                                }
                                label="ENR 400 Contractor"
                            />
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Site Security (%):</FormLabel>
                                </Grid>
                                <Grid item xs={INPUT_GRID_WIDTH} md={INPUT_LARGE_GRID_WIDTH}>
                                    <TextInput
                                        name="siteSecurity"
                                        variant="underlined"
                                        mask={negativeNumberMask}
                                        onChange={formik.handleChange}
                                        endAdornment="%"
                                        value={formik.values.siteSecurity || ''}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.siteSecurity}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Lead Carrier (%):</FormLabel>
                                </Grid>
                                <Grid item xs={INPUT_GRID_WIDTH} md={INPUT_LARGE_GRID_WIDTH}>
                                    <TextInput
                                        name="leadCarrier"
                                        variant="underlined"
                                        mask={negativeNumberMask}
                                        onChange={formik.handleChange}
                                        endAdornment="%"
                                        value={formik.values.leadCarrier || ''}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.leadCarrier}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="center">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <FormLabel>Construction Modifier (%):</FormLabel>
                                </Grid>
                                <Grid item xs={INPUT_GRID_WIDTH} md={INPUT_LARGE_GRID_WIDTH}>
                                    <TextInput
                                        name="constructionModifier"
                                        variant="underlined"
                                        mask={negativeNumberMask}
                                        onChange={formik.handleChange}
                                        endAdornment="%"
                                        value={formik.values.constructionModifier || ''}
                                        style={{ maxWidth: INPUT_MAX_WIDTH }}
                                        error={!!formik.errors.constructionModifier}
                                        onBlur={formik.handleBlur}
                                        readOnly={readOnly}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Spacing height="1rem" />
                            <Grid container direction="row" spacing={2} alignItems="start">
                                <Grid
                                    item
                                    xs={LABEL_GRID_WIDTH}
                                    md={LABEL_MD_GRID_WIDTH}
                                    lg={LABEL_LG_GRID_WIDTH}
                                    xl={LABEL_XL_GRID_WIDTH}
                                >
                                    <Typography variant="body1">Total Rate Adjustment:</Typography>
                                </Grid>
                                <Grid item xs={INPUT_GRID_WIDTH} md={INPUT_LARGE_GRID_WIDTH}>
                                    <Typography variant="body1">{formatPercent(totalRateAdjustment, 3)}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Spacing height="2rem" />
                    <Grid item>
                        <Typography
                            variant="body1"
                            style={{
                                fontWeight: 'bold',
                                borderTop: `1px solid ${COLORS.SECONDARY}`,
                                borderBottom: `1px solid ${COLORS.SECONDARY}`,
                                width: 'fit-content',
                            }}
                        >
                            Final Technical Rate: {formatDollar(technicalFinalRate, 3, false)}
                        </Typography>
                    </Grid>
                    <Spacing height="2rem" />
                    <Grid item>
                        <Grid container direction="row" spacing={2} alignItems="center">
                            <Grid
                                item
                                xs={LABEL_GRID_WIDTH}
                                md={LABEL_MD_GRID_WIDTH}
                                lg={LABEL_LG_GRID_WIDTH}
                                xl={LABEL_XL_GRID_WIDTH}
                            >
                                <FormLabel>Underwriter Adjusted Premium ($):</FormLabel>
                            </Grid>
                            <Grid item>
                                {/*<Grid item xs={INPUT_GRID_WIDTH} md={INPUT_LARGE_GRID_WIDTH}>*/}
                                <TextInput
                                    name="underwriterAdjustedPremium"
                                    variant="underlined"
                                    mask={currencyMask}
                                    onChange={formik.handleChange}
                                    value={formik.values.underwriterAdjustedPremium || ''}
                                    style={{ maxWidth: INPUT_MAX_WIDTH }}
                                    error={!!formik.errors.underwriterAdjustedPremium}
                                    onBlur={formik.handleBlur}
                                    readOnly={readOnly}
                                />
                            </Grid>
                        </Grid>
                        <Spacing height="1rem" />
                    </Grid>
                    <Grid container spacing={4}>
                        <Grid item>
                            <Typography variant="body1" color={COLORS.GRAY_DARK}>
                                Rate Adjustment:{' '}
                                {formatPercent((formik.values.underwriterAdjustmentFactor || 1) - 1, 1)}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body1" color={COLORS.GRAY_DARK}>
                                Final Rate: {formatDollar(formik.values.finalRate || 0, 3)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Automatic Policy Extension */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Automatic Policy Extension" />
                    <Grid container direction="column" spacing={4}>
                        <Grid item>
                            <Grid container>
                                <Grid container direction="row" alignItems="center" spacing={1}>
                                    <Grid item>
                                        <InputLabel htmlFor="extensionRate">Extension Rate ($):</InputLabel>
                                    </Grid>
                                    <Grid item width="8rem">
                                        <TextInput
                                            name="automaticExtensionRate"
                                            id="automaticExtensionRate"
                                            variant="underlined"
                                            onChange={handleCurrencyRateChange}
                                            value={formik.values.automaticExtensionRate || ''}
                                            error={!!formik.errors.automaticExtensionRate}
                                            onBlur={formik.handleBlur}
                                            readOnly={readOnly}
                                            mask={(value: string) => currencyRateMask(value, false)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Grid container direction="row" spacing={6}>
                                <Grid item>
                                    <Grid container direction="row" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <InputLabel htmlFor="automaticExtensionPeriod">
                                                Automatic Extension Period:
                                            </InputLabel>
                                        </Grid>
                                        <Grid item width="8rem">
                                            <TextInput
                                                variant="underlined"
                                                onChange={(e) => {
                                                    setAutomaticExtensionPeriodNumber(Number(e.target.value));
                                                }}
                                                value={automaticExtensionPeriodNumber}
                                                error={!!formik.errors.automaticExtensionPeriod}
                                                onBlur={formik.handleBlur}
                                                readOnly={readOnly}
                                                mask={numberMask}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <LrToggleButton
                                                value={automaticExtensionPeriodType}
                                                options={[
                                                    { value: 'months', label: 'Months' },
                                                    { value: 'days', label: 'Days' },
                                                ]}
                                                onChange={(value) => {
                                                    setAutomaticExtensionPeriodType(value);
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {/* Premiums Section */}
                <Grid item xs={12}>
                    <FormSectionHeader title="Premiums" />
                    <Grid container direction="row" spacing={6}>
                        <Grid item>
                            <FormLabel>Soft Cost Multiplier: {formik.values.softCostMultiplier}</FormLabel>
                        </Grid>
                        <Grid item>
                            <FormLabel>Loss of Rents Multiplier: {formik.values.lossOfRentsMultiplier}</FormLabel>
                        </Grid>
                        <Grid item>
                            <FormLabel>
                                Loss of Income Multiplier: {formik.values.lossOfGrossEarningsMultiplier}
                            </FormLabel>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Spacing height="2rem" />
            <BrLocationReviewPremiumTable
                locationBrReview={locationBrReview}
                location={location}
                onTriaChange={handleTriaChange}
                triaValue={formik.values.triaPercentage || locationBrReview?.triaPercentage || 0}
                submissionDetails={submission}
                readOnly={readOnly}
            />
        </form>
    );
};

export default BrLocationReviewPricingForm;
